/**
 * Platform Detection Utility
 * 
 * Provides platform detection for conditional code execution.
 * 
 * Usage:
 *   import { isWeb, isIOS, isAndroid, Platform } from '../utils/platform';
 *   
 *   if (isWeb) {
 *     // Web-specific code
 *   } else if (isIOS) {
 *     // iOS-specific code
 *   }
 */

// Detect if we're in React Native
// Check for React Native environment without requiring the module
const isReactNative = 
  typeof navigator !== 'undefined' && 
  navigator.product === 'ReactNative';

// Detect if we're in a browser
const isWeb = typeof window !== 'undefined' && !isReactNative;

// Detect iOS (web only - React Native detection happens at runtime)
let isIOS = false;
if (isWeb) {
  isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
} else if (isReactNative) {
  // In React Native, this will be set dynamically
  // We can't require react-native here as it breaks web builds
  try {
    // Only try to require if we're actually in React Native
    // This will be handled by the Platform object getter
    isIOS = false; // Will be set by Platform.OS getter
  } catch (e) {
    isIOS = false;
  }
}

// Detect Android (web only - React Native detection happens at runtime)
let isAndroid = false;
if (isWeb) {
  isAndroid = /Android/.test(navigator.userAgent);
} else if (isReactNative) {
  // In React Native, this will be set dynamically
  isAndroid = false; // Will be set by Platform.OS getter
}

// Detect mobile (any mobile platform)
const isMobile = isIOS || isAndroid;

// Detect desktop (web only, not mobile)
const isDesktop = isWeb && !isMobile;

/**
 * Get React Native Platform (lazy loaded to avoid breaking web builds)
 */
const getRNPlatform = () => {
  if (!isReactNative) return null;
  try {
    // Only require react-native when actually in React Native
    // This will fail gracefully in web builds
    return require('react-native').Platform;
  } catch (e) {
    // Not in React Native or module not available
    return null;
  }
};

/**
 * Platform information object
 */
export const Platform = {
  get OS() {
    if (isWeb) {
      return isIOS ? 'ios' : isAndroid ? 'android' : 'web';
    }
    if (isReactNative) {
      const RNPlatform = getRNPlatform();
      if (RNPlatform) {
        return RNPlatform.OS;
      }
    }
    return 'web';
  },
  isWeb,
  isReactNative,
  isIOS,
  isAndroid,
  isMobile,
  isDesktop,
  get version() {
    if (isReactNative) {
      const RNPlatform = getRNPlatform();
      if (RNPlatform) {
        return RNPlatform.Version;
      }
    }
    return null;
  }
};

// Export individual flags for convenience
export { isWeb, isReactNative, isIOS, isAndroid, isMobile, isDesktop };

export default Platform;

